import React from "react";
import { TicketStatus, TICKET_STATUS_DEFS, TicketPriority, Ticket, isClosedStatus } from "../../../api/support";

// Ceviri anahtarlari tek kaynaktan (dbo.TicketStatusTypes) turetilir.
export const STATUS_TEXT_KEYS: Record<TicketStatus, string> = Object.fromEntries(
    TICKET_STATUS_DEFS.map(d => [d.nr, d.labelKey]),
) as Record<TicketStatus, string>;
export const STATUS_COLORS: Record<TicketStatus, string> = {
    1: "bg-blue-100 text-blue-700", 2: "bg-sky-100 text-sky-700", 3: "bg-indigo-100 text-indigo-700",
    4: "bg-violet-100 text-violet-700", 5: "bg-amber-100 text-amber-700", 6: "bg-yellow-100 text-yellow-700",
    7: "bg-orange-100 text-orange-700", 8: "bg-red-100 text-red-700", 9: "bg-green-100 text-green-700",
    10: "bg-gray-100 text-gray-500", 11: "bg-teal-100 text-teal-700", 12: "bg-slate-100 text-slate-600",
    13: "bg-stone-100 text-stone-600", 14: "bg-rose-100 text-rose-700", 15: "bg-zinc-100 text-zinc-600",
};
export const STATUS_BADGE: Record<TicketStatus, string> = {
    1: "#3B82F6", 2: "#0EA5E9", 3: "#6366F1", 4: "#8B5CF6", 5: "#F59E0B",
    6: "#EAB308", 7: "#F97316", 8: "#EF4444", 9: "#10B981", 10: "#6B7280",
    11: "#14B8A6", 12: "#94A3B8", 13: "#A8A29E", 14: "#F43F5E", 15: "#71717A",
};
export const STATUS_DOT: Record<TicketStatus, string> = {
    1: "bg-blue-500", 2: "bg-sky-500", 3: "bg-indigo-500", 4: "bg-violet-500", 5: "bg-amber-500",
    6: "bg-yellow-500", 7: "bg-orange-500", 8: "bg-red-500", 9: "bg-emerald-500", 10: "bg-gray-400",
    11: "bg-teal-500", 12: "bg-slate-400", 13: "bg-stone-400", 14: "bg-rose-500", 15: "bg-zinc-500",
};
// Schema: 1=high, 2=medium, 3=low
export const PRIORITY_TEXT_KEYS: Record<TicketPriority, string> = {
    1: "priorityHigh", 2: "priorityNormal", 3: "priorityLow",
};
export const PRIORITY_COLORS: Record<TicketPriority, string> = {
    1: "bg-red-100 text-red-600",
    2: "bg-orange-100 text-orange-600",
    3: "bg-gray-100 text-gray-500",
};
export const PRIORITY_BADGE: Record<TicketPriority, string> = {
    1: "#EF4444",
    2: "#F97316",
    3: "#9CA3AF",
};
export const PRIORITY_DOT: Record<TicketPriority, string> = {
    1: "bg-red-500", 2: "bg-orange-500", 3: "bg-gray-400",
};
export const PRIORITY_ICON: Record<TicketPriority, string> = {
    1: "⚡", 2: "↑", 3: "↓",
};
export const PRODUCT_TEXT: Record<number, string> = {
    10: "mobiOffice", 11: "mobiSales", 12: "mobiShop", 14: "mobiRoute",
};
// Grid'de kisa tip kodu (ticketTypeNr -> kod). Tam ad tooltip'te / detayda gosterilir.
export const TYPE_SHORT: Record<number, string> = {
    1: "SUP", 2: "BUG", 3: "FEAT", 4: "SRV",
};
export const PRODUCT_COLOR: Record<number, string> = {
    10: "#6366F1",
    11: "#06B6D4",
    12: "#EC4899",
    14: "#14B8A6",
};
export const ACTION_LABEL_KEYS: Record<string, string> = {
    status_changed: "statusChanged",
    priority_changed: "priorityChanged",
    assigned: "assignmentMade",
};
export const ACTION_ICONS: Record<string, React.ReactNode> = {
    status_changed: React.createElement('svg', { className: "w-3.5 h-3.5", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24" },
        React.createElement('path', { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" })),
    priority_changed: React.createElement('svg', { className: "w-3.5 h-3.5", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24" },
        React.createElement('path', { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" })),
    assigned: React.createElement('svg', { className: "w-3.5 h-3.5", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24" },
        React.createElement('path', { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" })),
};

export function formatDate(dateStr: string, withYear = false) {
    return new Date(dateStr).toLocaleString("tr-TR", {
        day: "2-digit", month: "short", ...(withYear ? { year: "numeric" } : {}),
        hour: "2-digit", minute: "2-digit",
    });
}

export function getInitials(name: string): string {
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
}

// ── SLA ───────────────────────────────────────────────────────────────────────
// SLA durumu yalnizca due_date + status'tan turetilir; ayri bir backend alani yok.
// Acik bir ticketin son tarihine ne kadar kaldigina gore renk/eTicket uretir.

export type SlaState = "overdue" | "risk" | "ontrack" | "none";

export interface SlaInfo {
    state: SlaState;
    color: string;
    labelKey: string;        // durum eTicketi i18n anahtari ("" => gosterme)
    remainingMs: number | null; // due_date - now; negatif => gecikmis. none ise null.
}

// Bu esikten az sure kaldiysa ticket "SLA riski" sayilir (varsayilan 4 saat).
export const SLA_RISK_WINDOW_MS = 4 * 60 * 60 * 1000;

const SLA_MUTED = "var(--subtitle-text-color)";

export function getSlaInfo(
    ticket: Pick<Ticket, "due_date" | "status">,
    now: number = Date.now(),
): SlaInfo {
    // Kapali ticketta (RESOLVED/CLOSED/...) ya da son tarihi olmayanda SLA takibi yok.
    if (!ticket.due_date || isClosedStatus(ticket.status)) {
        return { state: "none", color: SLA_MUTED, labelKey: "", remainingMs: null };
    }
    const due = new Date(ticket.due_date).getTime();
    if (Number.isNaN(due)) {
        return { state: "none", color: SLA_MUTED, labelKey: "", remainingMs: null };
    }
    const remainingMs = due - now;
    if (remainingMs < 0) {
        return { state: "overdue", color: "#EF4444", labelKey: "slaOverdue", remainingMs };
    }
    if (remainingMs <= SLA_RISK_WINDOW_MS) {
        return { state: "risk", color: "#F97316", labelKey: "slaAtRisk", remainingMs };
    }
    return { state: "ontrack", color: "#10B981", labelKey: "slaOnTrack", remainingMs };
}

// "SLA Riski" filtresi: hem suresi gecmis hem de esik altinda kalanlari kapsar.
export function isSlaRisk(ticket: Pick<Ticket, "due_date" | "status">, now?: number): boolean {
    const s = getSlaInfo(ticket, now).state;
    return s === "overdue" || s === "risk";
}

// Kalan/gecikme suresini insan-okur formata cevirir: "2gün 3sa", "1sa 20dk", "30dk".
export function formatSlaRemaining(remainingMs: number, t: any): string {
    const totalMin = Math.floor(Math.abs(remainingMs) / 60000);
    const days = Math.floor(totalMin / 1440);
    const hours = Math.floor((totalMin % 1440) / 60);
    const mins = totalMin % 60;
    const parts: string[] = [];
    if (days > 0) parts.push(`${days}${t("daysShort")}`);
    if (hours > 0) parts.push(`${hours}${t("hoursShort")}`);
    // Gun varsa dakikayi gizle (gurultu olmasin); yoksa dakikayi goster.
    if (days === 0 && mins > 0) parts.push(`${mins}${t("minutesShort")}`);
    if (parts.length === 0) parts.push(`0${t("minutesShort")}`);
    return parts.join(" ");
}
